import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { ErsGridIdResolver } from '@rcs/grid';

/**
 * App-level {@link ErsGridIdResolver} that gives every `<ers-grid>` (without an
 * explicit `[gridId]`) a stable per-user persistence key derived from the route.
 *
 * Key = `<param-free route path>` + (if there are columns) `#<column signature>`.
 * The column signature is order-independent and includes hidden columns, so it
 * stays stable while the user reorders/hides/resizes columns, but differs for two
 * genuinely different grids that happen to share a route. This lets the wrapper
 * own persistence globally with no per-page wiring, while `@rcs/grid` itself
 * stays free of an `@angular/router` dependency.
 */
@Injectable({ providedIn: 'root' })
export class RouteErsGridIdResolver implements ErsGridIdResolver {
  private readonly router = inject(Router);

  resolve(ctx: { columnFields: string[] }): string | undefined {
    const path = this.routeConfigPath();
    if (!path) return undefined;
    const sig = this.columnSignature(ctx.columnFields);
    return sig ? `${path}#${sig}` : path;
  }

  /** Param-free path built from the configured `path` of each matched route segment. */
  private routeConfigPath(): string {
    const parts: string[] = [];
    let route: { routeConfig?: { path?: string } | null; firstChild?: unknown } | null =
      this.router.routerState.snapshot.root;
    while (route) {
      const seg = route.routeConfig?.path;
      if (seg) parts.push(seg);
      route = route.firstChild as typeof route;
    }
    return parts.join('/');
  }

  /** Small stable hash of the (sorted) column ids — independent of order/visibility/width. */
  private columnSignature(fields: string[]): string {
    if (!fields.length) return '';
    const s = [...fields].sort().join('|');
    let h = 0;
    for (let i = 0; i < s.length; i++) {
      h = (Math.imul(h, 31) + s.charCodeAt(i)) | 0;
    }
    return (h >>> 0).toString(36);
  }
}
